
import { Link } from "react-router-dom";
import { Globe, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useNavbarState } from "./useNavbarState";
import { useResponsiveTouchTargets } from "@/lib/mobile-utils";

interface MenuItem {
  label: string;
  path: string;
}

interface NavbarDesktopMenuProps {
  menuItems: MenuItem[];
}

const NavbarDesktopMenu = ({ menuItems }: NavbarDesktopMenuProps) => {
  const { isAuthenticated } = useAuth();
  const { language, handleLanguageChange } = useNavbarState();
  const { button, touchFeedback } = useResponsiveTouchTargets();

  const onNavigateToApp = () => {
    window.location.href = "/app";
  };

  return (
    <div className="flex items-center gap-3">
      {/* Language Dropdown with enhanced mobile support */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`${button} ${touchFeedback} focus-visible-enhanced`}
            aria-label="Selecionar idioma"
          >
            <Globe className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="dropdown-menu min-w-[150px] p-1"
          sideOffset={8}
        >
          <DropdownMenuItem 
            onClick={() => handleLanguageChange("pt")} 
            className={`${button} cursor-pointer ${touchFeedback} ${
              language === "pt" ? "bg-brand-primary/10 text-brand-primary" : ""
            }`}
          >
            <span className="flex items-center gap-2">
              🇧🇷 Português
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleLanguageChange("en")} 
            className={`${button} cursor-pointer ${touchFeedback} ${
              language === "en" ? "bg-brand-primary/10 text-brand-primary" : ""
            }`}
          >
            <span className="flex items-center gap-2">
              🇺🇸 English
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Authentication Buttons */}
      {isAuthenticated ? (
        <Button 
          onClick={onNavigateToApp} 
          className={`${button} bg-brand-primary text-white hover:bg-brand-primary/90 ${touchFeedback} focus-visible-enhanced`}
          aria-label="Acessar meu espaço"
        >
          <span>Meu Espaço</span>
        </Button>
      ) : (
        <>
          <Link to="/login">
            <Button 
              variant="ghost" 
              className={`${button} text-brand-primary hover:bg-brand-primary/10 ${touchFeedback} focus-visible-enhanced`}
            >
              <LogIn className="mr-2 h-4 w-4" />
              <span>Entrar</span>
            </Button>
          </Link>
          
          <Link to="/signup">
            <Button 
              className={`${button} bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:from-brand-secondary hover:to-brand-primary shadow-lg hover:shadow-xl transition-all duration-300 ${touchFeedback} focus-visible-enhanced`}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              <span>Começar Gratuitamente</span>
            </Button>
          </Link>
        </>
      )}
    </div>
  );
};

export default NavbarDesktopMenu;
